import type { HomeData } from "./TrialHomeScreen";

type DoseHeldReducedData = Pick<HomeData, "doseHeldReduced">;
type IpRelatedEventsData = Pick<
  HomeData,
  "ipAES" | "ipAES_grade3_teaes" | "ipAEs_SAEs"
>;
type CrfMetricsData = Pick<
  HomeData,
  "crf" | "crf_overdue" | "crf_required_sdv"
>;
type SiteSdvMetricsData = Pick<
  HomeData,
  | "site_sdv"
  | "site_sdv_average"
  | "site_sdv_std"
  | "site_sdv_min"
  | "site_sdv_max"
>;
type MvrActionItemsMetricsData = Pick<
  HomeData,
  | "mvr_action_items"
  | "mvr_action_items_open"
  | "mvr_action_items_open_60_days"
  | "mvr_action_items_closed"
>;
type ProtocolDeviationGroupsData = Pick<HomeData, "protocol_deviations">;
type IpAdministrationData = Pick<
  HomeData,
  "enrollment_data" | "trial_specific_data"
>;
type IpAdministrationItem = HomeData["enrollment_data"]["sex"][number];
type IpAdministrationDistribution = IpAdministrationItem[];
type PlotLabel = string | null | undefined;

const DEFAULT_EMPTY_LABEL = "No Data";

export const cloneTrialHomeData = (data: HomeData) =>
  JSON.parse(JSON.stringify(data)) as HomeData;

export const getDisplayLabel = (label: PlotLabel) => {
  if (label == null) {
    return DEFAULT_EMPTY_LABEL;
  }

  const normalizedLabel = String(label).trim();

  if (!normalizedLabel || normalizedLabel.toLowerCase() === "null") {
    return DEFAULT_EMPTY_LABEL;
  }

  return normalizedLabel;
};

export const getAggregateMetricValue = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (value && typeof value === "object") {
    const firstNumericValue = Object.values(
      value as Record<string, unknown>,
    ).find((entry) => typeof entry === "number");

    return typeof firstNumericValue === "number" ? firstNumericValue : 0;
  }

  return 0;
};

export const getDoseHeldReducedStacks = (data?: DoseHeldReducedData) =>
  data?.doseHeldReduced?.some(
    (item) =>
      item?.values?.HELD !== undefined || item?.values?.REDUCED !== undefined,
  )
    ? ["HELD", "REDUCED"]
    : ["Dose Held", "Dose Reduced"];

export const getIpRelatedEvents = (data: IpRelatedEventsData) =>
  data.ipAES?.length
    ? data.ipAES
    : [
        {
          name: "Grade 3 TEAEs",
          values: { total: data.ipAES_grade3_teaes ?? 0 },
        },
        {
          name: "SAEs",
          values: { total: data.ipAEs_SAEs ?? 0 },
        },
      ];

export const getCrfMetrics = (data: CrfMetricsData) => ({
  overdue: data.crf_overdue ?? data.crf?.overdue ?? 0,
  requiredSdv: data.crf_required_sdv ?? data.crf?.required_sdv ?? 0,
});

export const getSiteSdvMetrics = (data: SiteSdvMetricsData) => ({
  average: data.site_sdv_average ?? data.site_sdv?.average,
  standardDeviation: data.site_sdv_std ?? data.site_sdv?.std,
  minimum: data.site_sdv_min ?? data.site_sdv?.min,
  maximum: data.site_sdv_max ?? data.site_sdv?.max,
});

export const getMvrActionItemsMetrics = (data: MvrActionItemsMetricsData) => ({
  open: data.mvr_action_items_open ?? data.mvr_action_items?.open ?? 0,
  open60Days:
    data.mvr_action_items_open_60_days ??
    data.mvr_action_items?.open_60_days ??
    0,
  closed: data.mvr_action_items_closed ?? data.mvr_action_items?.closed ?? 0,
});

export const getIpAdministrationData = (
  data: IpAdministrationData,
): IpAdministrationDistribution => {
  if (data.enrollment_data.ipAdmin?.length) {
    return data.enrollment_data.ipAdmin;
  }

  const trialSpecificData = data.trial_specific_data;

  if (!trialSpecificData) {
    return [];
  }

  return Object.entries(trialSpecificData)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number")
    .map(([key, value]) => ({
      name: formatTrialSpecificKey(key),
      values: value,
    }));
};

const formatTrialSpecificKey = (value: string) =>
  value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getProtocolDeviationGroups = (
  data: ProtocolDeviationGroupsData,
) => {
  const deviations = data.protocol_deviations ?? {};

  let important = {};
  const other = Object.entries(deviations).reduce<Record<string, number>>(
    (acc, [severity, counts]) => {
      if (severity.toLowerCase() === "important") {
        important = counts;
        return acc;
      }
      Object.entries(counts).forEach(([status, count]) => {
        acc[status] = (acc[status] ?? 0) + count;
      });
      return acc;
    },
    {},
  );

  return { important, other };
};

export const getClosedDeviationCount = (
  deviations: Record<string, number> = {},
) => deviations.closed ?? deviations.Closed ?? 0;

export const getOpenDeviationCount = (
  deviations: Record<string, number> = {},
) =>
  Object.entries(deviations).reduce(
    (total, [status, count]) =>
      status === "closed" || status === "Closed" ? total : total + count,
    0,
  );
